import { useState } from 'react';
import {
  Grid,
  Typography,
  Collapse,
  makeStyles,
  createStyles,
  Menu,
  MenuItem,
  IconButton,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import SmileIcon from '@material-ui/icons/SentimentSatisfiedAlt';
import { Droppable } from 'react-beautiful-dnd';

import SharedIndicator from 'src/components/sidebar/SharedIndicator';
import TextInput from 'src/components/TextInput';
import { Dashboard } from 'src/types';
import { DashboardItem } from './DashboardItem';

export const useStyles = makeStyles(() =>
  createStyles({
    container: {
      padding: 0,
      alignItems: 'flex-start',
    },
    collapseTitle: {
      fontSize: 14,
      fontWeight: 400,
    },
    collapseCounter: {
      border: '1px solid #000000',
      borderRadius: 2,
      fontSize: 12,
      lineHeight: '12px',
      padding: 4,
      marginRight: 8,
    },
    collapseContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      margin: '8px 0',
      padding: 8,
      paddingLeft: 20,
      paddingRight: 8,
      position: 'relative',

      '&::before': {
        content: '""',
        position: 'absolute',
        left: 8,
        width: 1,
        top: 4,
        bottom: 4,
        borderLeft: '1px solid gray',
      },
    },
    nameContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      flex: 1,
    },
    titleInput: {
      '& .MuiInputBase-input': {
        fontSize: 14,
        fontWeight: 400,
        paddingTop: 0,
      },
    },
  })
);

interface DashboardFolderProps {
  name: string;
  dashboards: Dashboard[];
  onSelectDashboard: (dashboard: Dashboard) => void;
  onDeleteFolder?: (name: string) => void;
  onRenameFolder?: (name: string, newName: string) => void;
  onShareFolder?: (name: string) => void;
  onShareDashboard?: (dashboard: Dashboard) => void;
  onLeaveFolder?: (name: string) => void;
  onLeaveDashboard?: (dashboard: Dashboard) => void;
  /** Item-level received share ids (not folder-only). */
  leavableDashboardIds?: Set<string>;
  /** Read-only Shared section: Leave only, no drag targets. */
  sharedSection?: boolean;
  /** Owner-side: this folder is shared with other orgs. */
  isSharedOut?: boolean;
  /** Owner-side org count for this folder. */
  sharedOutOrganizationCount?: number;
  /** Owner-side item ids shared with other orgs → org count. */
  sharedOutDashboardIds?: Map<string, number>;
  isDashboardSelection?: boolean;
}

export const DashboardFolder = ({
  name,
  dashboards,
  isDashboardSelection = false,
  onDeleteFolder,
  onRenameFolder,
  onShareFolder,
  onShareDashboard,
  onLeaveFolder,
  onLeaveDashboard,
  leavableDashboardIds,
  sharedSection = false,
  isSharedOut = false,
  sharedOutOrganizationCount,
  sharedOutDashboardIds,
  onSelectDashboard,
}: DashboardFolderProps) => {
  const classes = useStyles();
  const [folderName, setFolderName] = useState<string>(name);
  const [isRenaming, setIsRenaming] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openFolders, setOpenFolders] = useState<boolean>(false);
  const openMenu = Boolean(anchorEl);
  const canLeaveFolder = Boolean(sharedSection && onLeaveFolder);
  const showOwnerMenu =
    !sharedSection &&
    !isDashboardSelection &&
    Boolean(onRenameFolder || onDeleteFolder || onShareFolder);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleRename = () => {
    setIsRenaming(true);
    handleCloseMenu();
  };

  const handleFinishEditing = () => {
    setIsRenaming(false);
    if (name !== folderName) {
      onRenameFolder?.(name, folderName);
    }
  };

  const handleOpenFolders = (flag: boolean) => {
    if (dashboards && dashboards.length) {
      setOpenFolders(flag);
    }
  };

  const header = (
    <Grid
      container
      item
      direction='row'
      justifyContent='space-between'
      alignItems='center'
      style={{ cursor: 'pointer' }}
    >
      <div
        className={classes.nameContainer}
        onClick={() => {
          !isRenaming && handleOpenFolders(!openFolders);
        }}
      >
        <SmileIcon htmlColor='#757575' style={{ fontSize: 18 }} />
        {isRenaming && !sharedSection ? (
          <TextInput
            className={classes.titleInput}
            value={folderName}
            onChange={setFolderName}
            onEnter={handleFinishEditing}
            onBlur={handleFinishEditing}
          />
        ) : (
          <>
            <Typography
              className={classes.collapseTitle}
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {name}
            </Typography>
            {!sharedSection && isSharedOut && (
              <SharedIndicator
                organizationCount={sharedOutOrganizationCount}
                onClick={
                  onShareFolder
                    ? (event) => {
                        event.stopPropagation();
                        onShareFolder(name);
                      }
                    : undefined
                }
              />
            )}
            <Typography
              className={classes.collapseTitle}
              style={{ flexShrink: 0 }}
            >
              ({dashboards.length || 0})
            </Typography>
          </>
        )}
      </div>
      {openFolders ? (
        <KeyboardArrowUpIcon
          onClick={() => {
            handleOpenFolders(!openFolders);
          }}
          style={{ fontSize: 18 }}
        />
      ) : (
        <KeyboardArrowDownIcon
          onClick={() => {
            handleOpenFolders(!openFolders);
          }}
          style={{ fontSize: 18 }}
        />
      )}
      {(showOwnerMenu || canLeaveFolder) && (
        <div>
          <IconButton
            onClick={handleOpenMenu}
            style={{ padding: 2, marginLeft: 10 }}
          >
            <MoreVertIcon htmlColor='#757575' style={{ fontSize: 16 }} />
          </IconButton>

          <Menu
            id='folder-menu'
            aria-labelledby='folder-button'
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleCloseMenu}
            onClick={handleCloseMenu}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            {showOwnerMenu && (
              <MenuItem onClick={handleRename}>Rename</MenuItem>
            )}
            {showOwnerMenu && onShareFolder && (
              <MenuItem
                onClick={() => {
                  onShareFolder(name);
                  handleCloseMenu();
                }}
              >
                Share
              </MenuItem>
            )}
            {showOwnerMenu && onDeleteFolder && (
              <MenuItem
                onClick={() => {
                  onDeleteFolder(name);
                  handleCloseMenu();
                }}
                style={{ color: '#ff0000' }}
              >
                Delete
              </MenuItem>
            )}
            {canLeaveFolder && (
              <MenuItem
                onClick={() => {
                  onLeaveFolder?.(name);
                  handleCloseMenu();
                }}
                style={{ color: '#ff0000' }}
              >
                Leave
              </MenuItem>
            )}
          </Menu>
        </div>
      )}
    </Grid>
  );

  const body = (
    <Collapse
      in={openFolders}
      timeout='auto'
      unmountOnExit
      style={{ width: '100%' }}
    >
      <Grid container className={classes.collapseContainer}>
        {dashboards.map((dashboard, index) => (
          <DashboardItem
            key={dashboard.id}
            index={index}
            dashboard={dashboard}
            noDraggable={sharedSection}
            onSelectDashboard={onSelectDashboard}
            onShareDashboard={sharedSection ? undefined : onShareDashboard}
            onLeaveDashboard={
              sharedSection
                ? undefined
                : leavableDashboardIds?.has(dashboard.id)
                ? onLeaveDashboard
                : undefined
            }
            hideSharedLabel={sharedSection}
            isSharedOut={
              !sharedSection && sharedOutDashboardIds?.has(dashboard.id)
            }
            sharedOutOrganizationCount={sharedOutDashboardIds?.get(
              dashboard.id
            )}
            showActions={!isDashboardSelection && !sharedSection}
          />
        ))}
      </Grid>
    </Collapse>
  );

  if (sharedSection) {
    return (
      <Grid container>
        {header}
        {body}
      </Grid>
    );
  }

  return (
    <Droppable droppableId={name}>
      {(provided) => (
        <Grid container ref={provided.innerRef} {...provided.droppableProps}>
          {header}
          <Collapse
            in={openFolders}
            timeout='auto'
            unmountOnExit
            style={{ width: '100%' }}
          >
            <Grid container className={classes.collapseContainer}>
              {dashboards.map((dashboard, index) => (
                <DashboardItem
                  key={dashboard.id}
                  index={index}
                  dashboard={dashboard}
                  onSelectDashboard={onSelectDashboard}
                  onShareDashboard={onShareDashboard}
                  onLeaveDashboard={
                    leavableDashboardIds?.has(dashboard.id)
                      ? onLeaveDashboard
                      : undefined
                  }
                  isSharedOut={sharedOutDashboardIds?.has(dashboard.id)}
                  sharedOutOrganizationCount={sharedOutDashboardIds?.get(
                    dashboard.id
                  )}
                  showActions={!isDashboardSelection}
                />
              ))}
              {provided.placeholder}
            </Grid>
          </Collapse>
        </Grid>
      )}
    </Droppable>
  );
};
